import type { ComboboxProps } from "@mantine/core";
import type {
  AnnotationsOptions,
  OptionsChartZoomingTypeValue,
  SelectDataObject,
} from "highcharts";
import { UserManager } from "oidc-client-ts";
import { type JSX, ReactNode } from "react";

import type { MeqColumnDef, MeqRowData } from "../components/MeqTable/types";
import type { GridType } from "./Gmp";
import PageId from "./PageId";
import type { Dayjs } from "./dayjs";

export enum DateFormat {
  DEFAULT = "DD MMM YYYY HH:mm:ss",
  DEFAULT_WITH_TIMEZONE = "DD MMM YYYY HH:mm:ss ([GMT] Z)",
  SHORT = "DD MMM YYYY",
  FULL = "DD MMM YYYY HH:mm:ss SSS Z",
  DAY_AND_TIME = "DD MMM HH:mm",
  DAY_AND_MONTH = "DD MMM",
  UNIX_TIMESTAMP = "x",
  BOOKINGS_LABEL = "[Week] w - MMM YYYY",
  // "Friday, 21 August 2026" — the weekday matters when reading a single
  // grader-day, since shift patterns differ by day.
  WEEKDAY_AND_DATE = "dddd, D MMMM YYYY",
}

export enum DateFormatUSA {
  DEFAULT = "MMM DD YYYY HH:mm:ss",
  DEFAULT_WITH_TIMEZONE = "MMM DD YYYY HH:mm:ss ([GMT] Z)",
  SHORT = "MMM DD YYYY",
  FULL = "MMM DD YYYY HH:mm:ss SSS Z",
  DAY_AND_TIME = "MMM DD HH:mm",
  DAY_AND_MONTH = "MMM DD",
  UNIX_TIMESTAMP = "x",
}

export enum DateFormatServer {
  DEFAULT = "YYYY-MM-DD HH:mm:ss",
  DEFAULT_WITH_TIMEZONE = "YYYY-MM-DD HH:mm:ss:SSS Z",
  SHORT = "YYYY-MM-DD",
  FULL = "YYYY-MM-DD HH:mm:ss SSS Z",
  DAY_AND_TIME = "MM-DD HH:mm",
  UNIX_TIMESTAMP = "x",
  TIMESTAMP = "YYYY-MM-DD HH:mm:ss.SSS",
}

/**
 * ISO 8601 UTC timestamp as the API sends it — never a `Date` on the client,
 * because Mongoose timestamps are serialised by `res.json()`. The fractionless
 * branch is not produced by `toISOString()`, but zod's `.datetime()` accepts it,
 * so it is included to keep the type honest if response validation is added.
 */
export type IsoDateTime =
  | `${number}-${number}-${number}T${number}:${number}:${number}Z`
  | `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;

// Per-route access/presentation metadata — the shape of each route's
// `staticData` (see the StaticDataRouteOption augmentation in `~/router.tsx`).
export type RouteStaticData = {
  pageId: PageId;
  titleKey: string; // i18n translation key for the title
  /** Registry nav feature the route belongs to; the route guard tests it. */
  navId?: string;
  hideTitle?: boolean;
  hideNav?: boolean;
};

export type Org =
  | {
      _id: string;
      createdAt: IsoDateTime;
      updatedAt: IsoDateTime;
      idString: string;
      country: Country;
      displayName: string;
      description: string;
      isTCRequired: boolean;
      isValid: boolean;
      isCurrent?: boolean;
      zitadelOrgIdString?: string;
      organisationId?: string;
      // Customer-level WorkOS org of this site's parent organisation,
      // resolved server-side by /user/UserOrgs. Absent on the Zitadel path.
      workosOrgId?: string | null;
    }
  | null
  | undefined;

export type ZitadelOrg =
  | {
      idString: string;
      authority: string;
      projectResourceId: string;
      reactClientId: string;
      reactRedirectUri: string;
    }
  | null
  | undefined;

export enum ZitadelOrgIdString {
  GMP = "GMP",
  MEQ = "MEQ",
  JBS = "JBS",
}

export interface ZitadelAuth {
  authorize(): Promise<void>;
  signout(): Promise<void>;
  userManager: UserManager;
}

export interface Message {
  readonly text: string | null;
  readonly type: AlertColor;
}

/** Snackbar/alert severity (formerly MUI's AlertColor). */
export type AlertColor = "success" | "info" | "warning" | "error";

export interface FormData {
  [prop: string]: unknown;
}

export enum FormInputCategory {
  AUTO_COMPLETE = "AutoComplete",
  CHECK_BOX = "CheckBox",
  COMPONENT = "Component",
  COMPONENT_FUNCTION = "ComponentFunction",
  DATE_PICKER = "DatePicker",
  DATE_RANGE_PICKER = "DateRangePicker",
  RADIO_GROUP = "RadioGroup",
  SELECT = "Select",
  TEXT_FIELD = "TextField",
}

export type FormInputItem = {
  name: string;
  label?: ReactNode;
  category: FormInputCategory;
  defaultValue?: unknown;
  value?: unknown;
  placeholder?: string;
  helperText?: ReactNode;
  helperTextSx?: object;
  inputProps?: object;
  textFieldProps?: object;
  validationParams?: object;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- callback receives heterogeneous form values; callers pass typed setters that can't accept `unknown`
  exposeValue?: (value: any) => void;
  component?: JSX.Element;
  componentFunction?: (formMeta: LooseObject) => JSX.Element;
  type?: string; // can be password, search, number
  required?: boolean;
  multiline?: boolean;
  size?: "small" | "medium" | undefined;
  avoidValidation?: boolean;
  options?: readonly LabelValuePair[];
  disabled?: boolean;
  disabledWhen?: (values: LooseObject) => boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  multiple?: boolean;
  format?: string;
  loading?: boolean;
  selectAll?: boolean; // select all options of a select
  gridLayout?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  groupBy?: (option: string | LooseObject) => string;
  getOptionLabel?: (option: string | LooseObject) => string;
  showDefaultValueWhenEmpty?: boolean;
  // MUI-Autocomplete-era signature; the Mantine renderer calls it with
  // (props, option) only, so the trailing params are optional for existing
  // 4-arg call-site declarations.
  autoCompleteRenderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: LabelValuePair | string,
    state?: unknown,
    ownerState?: unknown,
  ) => React.ReactNode;
  autoCompleteShowSearchIcon?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI createFilterOptions callers use varying generics (LooseObject, LabelValuePair); unifying requires updating all callsites
  autoCompleteFilterOptions?: any;
  autoCompleteExposeInputValue?: (inputValue: string) => void;
  /** Shows a clear button on a single autocomplete, so a picked value can be emptied. */
  autoCompleteClearable?: boolean;
  autoCompleteShowInputValue?: boolean;
  /** Forwarded to the Mantine Select/MultiSelect, e.g. `withinPortal: false`
   *  for a form hosted inside a Popover. */
  comboboxProps?: ComboboxProps;
  hidden?: boolean;
};

export type LabelValuePair = {
  label: ReactNode;
  value: unknown;
  description?: ReactNode;
  disabled?: boolean;
  businessName?: string;
};

export type LooseObject = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- changing to `unknown` causes 495 type errors across 147 files; requires a dedicated migration
  [key: string]: any;
};

export enum MessageText {
  SUCCESS = "Mission complete",
  INPUT_ERROR_REQUIRED = "Required",
  INPUT_ERROR_EMAIL = "Please type in a valid email address",
  INPUT_ERROR_JSON = "Please type in a valid json string",
  INPUT_ERROR_NUMBER = "Should be a number",
  INPUT_ERROR_POSITIVE_NUMBER = "Should be a positive number",
  NUMBER_ONLY = "Number only please",
  NO_ORG_FOUND = "No organisation found. Please contact your admin.",
  PAGE_NOT_FOUND = "Page not found.",
  NETWORK_ERROR = "Network error",
}

export enum PasswordLength {
  MIN = 4,
  MAX = 50,
}

export interface ServerRequest {
  action: string;
  params?: object;
  token?: string;
  zitadelOrgIdString?: string;
}

export interface ServerResponse {
  message: Message;
  serverData: LooseObject | LooseObject[] | null;
  statusCode?: number;
}

export enum ServerResponseStatusCode {
  SUCCESS = 700,
  FAILED = 701,
}

export type JsDataType = "string" | "number" | "date" | "datetime" | undefined;

export type QueryResultColumn = {
  name: string;
  label: string;
  type: JsDataType;
  scale: number | null;
};

export type QueryResult = {
  err?: unknown;
  rows?: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any -- row shape varies per query; typed at consumption sites
  columns?: QueryResultColumn[];
  numRows?: number;
  numUpdatedRows?: number;
  requestId?: string;
};

export type QueryResultTable = QueryResult & {
  tableColumns: MeqColumnDef<MeqRowData, unknown>[];
  tableRows: MeqRowData[];
};

export enum GmpBookingStatus {
  NotApplicable = "Not Applicable",
  ForwardContract = "Forward Contract",
  ScheduledHigh = "Scheduled High",
  ScheduledLow = "Scheduled Low",
  Assigned = "Assigned",
  Completed = "Completed",
}

export enum ChartType { // refer to PlotType to assign value
  BAR_CHART = "bar",
  BOX = "box",
  SCATTER_PLOT = "scatter",
  HISTOGRAM = "histogram",
  LINE_CHART = "line",
  PIE_CHART = "pie",
  DONUT_CHART = "donut",
  VIOLIN_PLOT = "violin",
}

export type ChartTrace = {
  nameX?: string;
  nameY?: string;
  x?: unknown;
  y?: unknown;
  trendline?: boolean;
};

export type ChartData = {
  xTitle: string;
  yTitle: string;
  data: ChartTrace[];
  xCategoryOrder?:
    | "trace"
    | "category ascending"
    | "category descending"
    | "array"
    | "total ascending"
    | "total descending"
    | "min ascending"
    | "min descending"
    | "max ascending"
    | "max descending"
    | "sum ascending"
    | "sum descending"
    | "mean ascending"
    | "mean descending"
    | "median ascending"
    | "median descending";
  xCategoryArray?: unknown[];
  yAxisRange?: unknown[];
};

export type ChartProps = {
  type: ChartType;
  title?: string;
  headerBeforeLoading?: ReactNode;
  header?: ReactNode;
  data: ChartData;
  loading?: boolean;
  minHeight?: number;
  updateFilter?: (v: unknown) => void;
  org?: Org;
  chartTraceColors?: string[];
  style?: LooseObject;
  bgcolor?: string;
  backdrop?: ReactNode;
  legendX?: number;
  legendY?: number;
};

export type Trace = {
  name: string;
  data: LooseObject[]; // this is an array of object
  interestedAttibutes?: string[];
  dataFormatted?: LooseObject[];
  showInLegend?: boolean;
  seriesType?: string;
  color?: string;
  enableMouseTracking?: boolean;
  markerSymbol?: string;
  markerRadius?: number;
  tooltip?: LooseObject;
};

export type AxisSwitchProps = {
  optionsOfXAxis?: QueryResultColumn[];
  optionsOfYAxis?: QueryResultColumn[];
  defaultXAxisName?: string;
  defaultYAxisName?: string;
  exposeXAxis?: (v: QueryResultColumn | undefined) => void;
  exposeYAxis?: (v: QueryResultColumn | undefined) => void;
  handleChartStateChangeXAxis?: (v: QueryResultColumn | undefined) => void;
  handleChartStateChangeYAxis?: (v: QueryResultColumn | undefined) => void;
};

export type SelectedChartData = {
  xAxis?: SelectDataObject | null;
  yAxis?: SelectDataObject | null;
};

export type HchartsProps = {
  /** Stable test handle stamped on the chart card, scheme `chart:<page>:<name>` */
  testId?: string;
  loading?: boolean;
  type: ChartType;
  histnorm?: "percent" | "probability" | "density" | undefined;
  stacking?: Highcharts.OptionsStackingValue;
  title: ReactNode;
  titleExtra?: JSX.Element;
  xTitle?: string;
  yTitle?: string;
  xAxis?: QueryResultColumn;
  yAxis?: QueryResultColumn;
  yAxisMin?: number;
  yAxisMax?: number;
  yAxisAllowDecimals?: boolean;
  xAxisMin?: number;
  xAxisMax?: number;
  xAxisType?: "date" | "-" | "linear" | "log" | "category" | "multicategory";
  yAxisType?: "date" | "-" | "linear" | "log" | "category" | "multicategory";
  xCategoryOrder?:
    | "trace"
    | "category ascending"
    | "category descending"
    | "array"
    | "total ascending"
    | "total descending"
    | "min ascending"
    | "min descending"
    | "max ascending"
    | "max descending"
    | "sum ascending"
    | "sum descending"
    | "mean ascending"
    | "mean descending"
    | "median ascending"
    | "median descending";
  data: Trace[];
  xData?: string[]; // for some charts having fixed x axis
  header?: JSX.Element;
  axisSwitch?: AxisSwitchProps;
  hideAxisSwitch?: boolean;
  valueSuffix?: string;
  traceColors?: string[];
  pointFormatExtra?: string;
  height?: number | string;
  zoomingType?: OptionsChartZoomingTypeValue;
  hideLegend?: boolean;
  annotations?: Array<AnnotationsOptions>;
  subCharts?: Partial<HchartsProps>[];
  onSelect?: (chartOptionsWithSelectedData: HchartsProps[]) => void;
  updateFilter?: (v: SelectedChartData) => void;
  hideToolButtons?: boolean;
  onSeriesClick?: (event: Highcharts.SeriesClickEventObject) => void;
  inverted?: boolean;
  showSeriesDataLabelsOnHover?: boolean;
};

export enum QueryType {
  ALL_TABLES = "ALL TABLES",
  ALL_VIEWS = "All Views",
  ALL_COLUMNS = "All Columns",
  DISTINCT_VALUES_OF_COLUMN = "DISTINCT_VALUES_OF_COLUMN",
  ORDINARY_QUERY = "Ordinary Query",
  ORIGINAL_QUERY = "Original Query",
}

export type DateRange = {
  from: Dayjs;
  to: Dayjs;
};

export enum Country {
  AU = "AU",
  BR = "BR",
  NZ = "NZ",
  UK = "UK",
  US = "US",
}

export type DataPoolItem = {
  id: number | string; // it is a hash of all other attributes
  params: LooseObject;
  data: QueryResult;
  updatedAt: number; // milliseconds
};

export type GmpGridPrice = {
  rowNumber: number;
  hscwCategory: string;
  lmyCategory1: number | "";
  lmyCategory2: number | "";
};

export type GlGridPrice = {
  rowNumber: number;
  hscwCategory: string;
  lmyCategory1: number | "";
  lmyCategory2: number | "";
  lmyCategory3: number | "";
  lmyCategory4: number | "";
  lmyCategory5: number | "";
};

type BaseGridPricingDataType = {
  gridId?: string;
  year?: number;
  week?: number;
  createdAt?: Date;
  updatedAt?: Date;
  gridType: GridType;
};

type GridPricingDataTypeGL = BaseGridPricingDataType & {
  prices: GlGridPrice[];
  gridType: "GL";
};
// } satisfies BaseGridPricingDataType;
type GridPricingDataTypeGMP = BaseGridPricingDataType & {
  prices: GmpGridPrice[];
  gridType: "GMP";
};
// } satisfies BaseGridPricingDataType;

export type GridPricingDataType =
  | GridPricingDataTypeGL
  | GridPricingDataTypeGMP;

export type GlossaryItemType = {
  title: string;
  content: string;
  link?: string;
  extra?: JSX.Element;
};
